import { useState, useEffect } from 'react'
import { X, Send, Check, Clock } from 'lucide-react'
import { ROLES, STAGE_FOCI, CHECK_SIZES } from '../data/seed'

const STORAGE_KEY = 'investorProfile'

function loadProfile() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw)
  } catch {
    return null
  }
}

function saveProfile(data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  } catch {
    // localStorage may be unavailable (private mode, quota) — silently skip
  }
}

const EMPTY = { name: '', fund: '', email: '', role: ROLES[0], stageFocus: [], checkSize: CHECK_SIZES[0] }

export default function AccessRequestModal({ open, onClose, company, onSubmit }) {
  const [form, setForm] = useState(EMPTY)
  const [errors, setErrors] = useState({})
  const [submitted, setSubmitted] = useState(false)

  // Auto-fill from localStorage on mount
  useEffect(() => {
    if (!open) return
    const saved = loadProfile()
    if (saved) {
      setForm({
        name: saved.name || '',
        fund: saved.fund || '',
        email: saved.email || '',
        role: saved.role || ROLES[0],
        stageFocus: saved.stageFocus || [],
        checkSize: saved.checkSize || CHECK_SIZES[0],
      })
    }
  }, [open])

  if (!open) return null

  const reset = () => {
    setForm(EMPTY)
    setErrors({})
    setSubmitted(false)
  }

  const handleClose = () => { reset(); onClose?.() }

  const toggleStage = (stage) => {
    setForm((p) => ({
      ...p,
      stageFocus: p.stageFocus.includes(stage) ? p.stageFocus.filter((s) => s !== stage) : [...p.stageFocus, stage],
    }))
  }

  const validate = () => {
    const e = {}
    if (!form.name.trim()) e.name = 'Full name is required'
    if (!form.fund.trim()) e.fund = 'Fund name is required'
    if (!form.email.trim()) e.email = 'Email is required'
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Enter a valid email address'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const submit = (e) => {
    e.preventDefault()
    if (!validate()) return
    saveProfile(form)
    onSubmit?.(form)
    setSubmitted(true)
    setTimeout(handleClose, 1800)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-ink/40" onClick={handleClose} aria-hidden />
      <div className="relative bg-surface border border-neutral-200 w-full max-w-md max-h-[90vh] overflow-y-auto animate-fade-in">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
          <div>
            <h2 className="text-sm font-semibold text-ink">Request Vault Access</h2>
            {company && <p className="text-xs text-muted mt-0.5">{company.name}</p>}
          </div>
          <button onClick={handleClose} className="text-neutral-400 hover:text-ink" aria-label="Close"><X size={18} /></button>
        </div>

        {submitted ? (
          <div className="px-6 py-10 text-center">
            <div className="w-12 h-12 bg-brand-soft mx-auto flex items-center justify-center mb-4">
              <Check size={22} className="text-brand" />
            </div>
            <p className="text-sm font-semibold text-ink">Access request sent to founder</p>
            <p className="text-xs text-muted mt-1">The founder will review your request and grant vault access upon approval.</p>
            <div className="mt-4 inline-flex items-center gap-1.5 text-xs text-amber-700">
              <Clock size={13} /> Access Request Pending Founder Approval
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="px-6 py-5 space-y-5">
            <div>
              <label className="label-above">Full Name</label>
              <input type="text" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} className="input-flat" placeholder="Your full name" />
              {errors.name && <p className="text-[11px] text-rose-600 mt-1">{errors.name}</p>}
            </div>
            <div>
              <label className="label-above">Fund / Firm Name</label>
              <input type="text" value={form.fund} onChange={(e) => setForm((p) => ({ ...p, fund: e.target.value }))} className="input-flat" placeholder="Your firm" />
              {errors.fund && <p className="text-[11px] text-rose-600 mt-1">{errors.fund}</p>}
            </div>
            <div>
              <label className="label-above">Institutional Email</label>
              <input type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} className="input-flat" placeholder="you@firm.vc" />
              {errors.email && <p className="text-[11px] text-rose-600 mt-1">{errors.email}</p>}
            </div>
            <div>
              <label className="label-above">Role</label>
              <select value={form.role} onChange={(e) => setForm((p) => ({ ...p, role: e.target.value }))} className="input-flat">
                {ROLES.map((r) => (<option key={r}>{r}</option>))}
              </select>
            </div>
            <div>
              <label className="label-above">Investment Stage Focus</label>
              <div className="flex flex-wrap gap-2">
                {STAGE_FOCI.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => toggleStage(s)}
                    className={`px-3 py-1.5 text-xs font-semibold border transition-colors ${
                      form.stageFocus.includes(s) ? 'border-brand bg-brand-soft text-brand' : 'border-neutral-200 text-neutral-500 hover:text-ink'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="label-above">Check Size Range</label>
              <select value={form.checkSize} onChange={(e) => setForm((p) => ({ ...p, checkSize: e.target.value }))} className="input-flat">
                {CHECK_SIZES.map((c) => (<option key={c}>{c}</option>))}
              </select>
            </div>
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={handleClose} className="btn-secondary">Cancel</button>
              <button type="submit" className="btn-primary flex items-center gap-1.5">
                <Send size={14} /> Send Access Request
              </button>
            </div>
            <p className="text-[11px] text-muted text-center pt-1">Your details have been saved for a faster experience next time.</p>
          </form>
        )}
      </div>
    </div>
  )
}
